import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch, FiExternalLink, FiTrash2, FiRefreshCw, FiFileText,
  FiAlertCircle, FiPlus,
} from 'react-icons/fi';
import './views.css';

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const AllPagesView = ({ filterStatus, onCreateNew }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = filterStatus ? `/api/pages?status=${filterStatus}` : '/api/pages';
      const res = await fetch(url);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load pages');
      setPages(json.data);
    } catch (err) {
      setError(err.message || 'Could not reach the API.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const remove = async (slug) => {
    if (!window.confirm(`Delete page "${slug}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/pages/${slug}`, { method: 'DELETE' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Delete failed');
      setPages((prev) => prev.filter((p) => p.slug !== slug));
    } catch (err) {
      alert(err.message);
    }
  };

  const filtered = pages.filter((p) => {
    if (category !== 'all' && p.category !== category) return false;
    if (search && !`${p.title} ${p.slug} ${p.h1}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const headerLabel = filterStatus === 'draft' ? 'Drafts' : 'All Pages';

  return (
    <div className="vw">
      <motion.div className="vw-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="vw-card-head vw-card-head-row">
          <div>
            <h2>{headerLabel}</h2>
            <p>
              {pages.length.toLocaleString()} page{pages.length === 1 ? '' : 's'}
              {filterStatus ? ` · status: ${filterStatus}` : ' · all statuses'}
            </p>
          </div>
          <div className="vw-head-actions">
            <button className="vw-btn vw-btn-ghost vw-btn-sm" onClick={load} title="Refresh">
              <FiRefreshCw /> Refresh
            </button>
            {onCreateNew && (
              <button className="vw-btn vw-btn-primary vw-btn-sm" onClick={onCreateNew}>
                <FiPlus /> New Page
              </button>
            )}
          </div>
        </div>

        <div className="vw-toolbar">
          <div className="vw-search">
            <FiSearch />
            <input
              placeholder="Search title, slug or H1…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="vw-toolbar-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="all">All categories</option>
            <option value="panchangam">Panchangam</option>
            <option value="horoscope">Horoscope</option>
            <option value="matchmaking">Match Making</option>
            <option value="astrology">Generic Astrology</option>
          </select>
        </div>

        {error && (
          <div className="vw-error vw-error-block">
            <FiAlertCircle /> {error}
            <button className="vw-btn vw-btn-ghost vw-btn-sm" onClick={load}>Retry</button>
          </div>
        )}

        {loading ? (
          <div className="vw-empty">
            <span className="vw-spinner vw-spinner-lg" />
            Loading pages…
          </div>
        ) : filtered.length === 0 ? (
          <div className="vw-empty">
            <FiFileText className="vw-empty-icon" />
            {pages.length === 0
              ? 'No pages generated yet. Click "New Page" to create the first one.'
              : 'No pages match your filters.'}
          </div>
        ) : (
          <div className="vw-table-wrap">
            <table className="vw-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Words</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="vw-title-cell">
                        <strong>{p.h1}</strong>
                        <code className="vw-slug">/blog/{p.slug}</code>
                      </div>
                    </td>
                    <td><span className="vw-tag">{p.categoryLabel}</span></td>
                    <td>{p.wordCount.toLocaleString()}</td>
                    <td>
                      <span className={`vw-status vw-status-${p.status}`}>{p.status}</span>
                    </td>
                    <td className="vw-muted">{fmt(p.createdAt)}</td>
                    <td>
                      <div className="vw-row-actions">
                        <a
                          className="vw-icon-btn"
                          href={`/blog/${p.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          title="View live"
                        >
                          <FiExternalLink />
                        </a>
                        <button
                          className="vw-icon-btn vw-danger"
                          onClick={() => remove(p.slug)}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AllPagesView;
